/**
 * Reiter „Profil" der Konto-Seite: Name, E-Mail-Adresse, Sprache.
 *
 * Die drei Felder haben bewusst drei verschiedene Wege:
 *
 *   - **Name** — sofort wirksam (`PATCH /api/v1/me`), mit Audit-Eintrag.
 *   - **E-Mail** — nur ueber einen Bestaetigungslink an die **neue** Adresse
 *     (`authClient.changeEmail`). Wer sich vertippt, aendert nichts. Solange
 *     der System-Mailer auf `mock` steht, kommt real keine Mail an; deshalb
 *     der Hinweis unter dem Feld statt eines Sonderfalls im UI.
 *   - **Sprache** — schaltet sofort um und wird im Konto gemerkt
 *     (`lib/locale.ts`, damit Kopfzeile und Konto dasselbe tun).
 */
import { changeEmailSchema, LOCALES, profileNameSchema, type Locale } from '@tickethub/shared';
import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { authClient } from '@/lib/auth-client';
import { toastError } from '@/lib/errors';
import { useLocalePreference } from '@/lib/locale';
import { meApi } from '@/lib/me';
import { useSession } from '@/lib/session';

export function AccountProfile() {
  const { t } = useTranslation();
  const { user, applyUser } = useSession();
  const { locale, setLocale } = useLocalePreference();

  // `null` = noch nichts getippt; dann zeigt das Feld den Stand aus der
  // Sitzung. So steht der Name auch dann drin, wenn sie erst nach dem ersten
  // Rendern eintrifft — ohne Effekt, der State hinterherzieht.
  const [typedName, setTypedName] = useState<string | null>(null);
  const name = typedName ?? user?.name ?? '';
  const [nameError, setNameError] = useState<string | null>(null);
  const [namePending, setNamePending] = useState(false);

  const [newEmail, setNewEmail] = useState('');
  const [emailError, setEmailError] = useState<string | null>(null);
  const [emailPending, setEmailPending] = useState(false);

  async function saveName(event: FormEvent) {
    event.preventDefault();
    setNameError(null);

    const parsed = profileNameSchema.safeParse(name);
    if (!parsed.success) {
      setNameError(t('account.name_required'));
      return;
    }

    setNamePending(true);
    try {
      applyUser(await meApi.updateProfile({ name: parsed.data }));
      // Zurueck auf „nichts getippt": ab jetzt gilt wieder der Stand aus der
      // Sitzung — und der ist gerade derselbe.
      setTypedName(null);
      toast.success(t('account.name_saved'));
    } catch (error) {
      toastError(error, t);
    } finally {
      setNamePending(false);
    }
  }

  async function requestEmailChange(event: FormEvent) {
    event.preventDefault();
    setEmailError(null);

    const parsed = changeEmailSchema.safeParse({ new_email: newEmail });
    if (!parsed.success) {
      setEmailError(t('account.email_invalid'));
      return;
    }
    if (parsed.data.new_email.toLowerCase() === user?.email.toLowerCase()) {
      setEmailError(t('account.email_unchanged'));
      return;
    }

    setEmailPending(true);
    try {
      const result = await authClient.changeEmail({
        newEmail: parsed.data.new_email,
        // Der Link aus der Mail landet nach der Bestaetigung wieder hier.
        callbackURL: `${window.location.origin}/account`,
      });
      if (result.error) {
        setEmailError(t('account.email_change_failed'));
        return;
      }
      setNewEmail('');
      toast.success(t('account.email_link_sent'));
    } finally {
      setEmailPending(false);
    }
  }

  return (
    <div className="max-w-xl space-y-6">
      {/* ── Name ──────────────────────────────────────────────────────── */}
      <section className="space-y-3 rounded-lg border p-4" aria-labelledby="account-name-heading">
        <h2 className="text-lg font-medium" id="account-name-heading">
          {t('account.name')}
        </h2>
        <p className="text-sm text-muted-foreground">{t('account.name_hint')}</p>

        {/* `noValidate`: die Meldungen kommen uebersetzt aus den Zod-Schemas,
            nicht als native Sprechblase des Browsers (frontend-ux.md). */}
        <form className="space-y-4" noValidate onSubmit={(event) => void saveName(event)}>
          <div className="space-y-2">
            <Label htmlFor="account-name">{t('account.name')}</Label>
            {/* Kein `required`: die native Sprechblase des Browsers ist
                unuebersetzt und faengt „nur Leerzeichen" ohnehin nicht ab
                (frontend-ux.md). Die Pruefung steht unten am Feld. */}
            <Input
              id="account-name"
              autoComplete="name"
              aria-invalid={nameError !== null}
              aria-describedby={nameError ? 'account-name-error' : undefined}
              value={name}
              onChange={(event) => setTypedName(event.target.value)}
            />
          </div>
          {nameError ? (
            <p className="text-sm text-destructive" id="account-name-error">
              {nameError}
            </p>
          ) : null}
          <Button type="submit" disabled={namePending}>
            {namePending ? t('common.saving') : t('common.save')}
          </Button>
        </form>
      </section>

      {/* ── E-Mail-Adresse ────────────────────────────────────────────── */}
      <section className="space-y-3 rounded-lg border p-4" aria-labelledby="account-email-heading">
        <h2 className="text-lg font-medium" id="account-email-heading">
          {t('account.email')}
        </h2>
        <p className="text-sm text-muted-foreground">
          {t('account.email_current', { email: user?.email ?? '' })}
        </p>

        <form className="space-y-4" noValidate onSubmit={(event) => void requestEmailChange(event)}>
          <div className="space-y-2">
            <Label htmlFor="account-new-email">{t('account.new_email')}</Label>
            <Input
              id="account-new-email"
              type="email"
              autoComplete="email"
              aria-invalid={emailError !== null}
              aria-describedby={emailError ? 'account-email-error' : undefined}
              value={newEmail}
              onChange={(event) => setNewEmail(event.target.value)}
            />
            <p className="text-xs text-muted-foreground">{t('account.email_verify_hint')}</p>
          </div>
          {emailError ? (
            <p className="text-sm text-destructive" id="account-email-error">
              {emailError}
            </p>
          ) : null}
          <Button type="submit" disabled={emailPending}>
            {emailPending ? t('common.saving') : t('account.email_submit')}
          </Button>
        </form>
      </section>

      {/* ── Sprache (Spec 14) ─────────────────────────────────────────── */}
      <section
        className="space-y-3 rounded-lg border p-4"
        aria-labelledby="account-language-heading"
      >
        <h2 className="text-lg font-medium" id="account-language-heading">
          {t('language.label')}
        </h2>
        <p className="text-sm text-muted-foreground">{t('account.language_hint')}</p>

        <div className="space-y-2">
          <Label htmlFor="account-language">{t('language.label')}</Label>
          <Select
            id="account-language"
            value={locale}
            onChange={(event) => setLocale(event.target.value as Locale)}
          >
            {LOCALES.map((entry) => (
              <option key={entry} value={entry}>
                {t(`language.${entry}`)}
              </option>
            ))}
          </Select>
        </div>
      </section>
    </div>
  );
}
